import { useCallback, useState } from 'react'
import { ReservationF } from '../../../../types/reservation-detail.types'
import { useTenant } from 'src/app/hooks/context/context.hook'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { useTranslation } from 'react-i18next'
import { BillCategoryType, useMoveProductionAssignedMutation } from 'src/api/api.generated'
import { FormItem, HICLoading } from 'hic-components'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { SelectReservationDestinationBill } from './reservation-destination-bill-select'
import { Stack } from '@vapor/material'
import { UnionBill } from './production-assigned-actions.type'

type ProductionAssignedActionMoveProps = {
  title: string
  open: boolean
  onClose: () => void
  bills: UnionBill[]
  productionAssignedIds: string[]
  originBillCategory?: BillCategoryType
  reservationId: string[]
  reservedResourceId?: string[]
  reservation?: ReservationF
  sourceBillId: string
}
export function ProductionAssignedActionMove({
  title,
  open,
  onClose,
  bills,
  productionAssignedIds,
  reservationId,
  reservedResourceId,
  originBillCategory,
  reservation,
  sourceBillId,
}: ProductionAssignedActionMoveProps) {
  const { t } = useTranslation()
  const { id: tenantId } = useTenant()
  const showError = useErrorSnackBar()
  const showSuccessSnackBar = useSuccessSnackBar()
  const [destinationBillId, setDestinationBillId] = useState<string | null>()

  const [moveMutation, { loading }] = useMoveProductionAssignedMutation()

  const moveProductionAssigned = useCallback(async () => {
    if (destinationBillId) {
      const result = await moveMutation({
        variables: {
          input: {
            tenantId: tenantId,
            destinationBill: destinationBillId,
            productionAssigneds: productionAssignedIds,
          },
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onError() {},
        refetchQueries: ['ReservationDetail'],
      })
      if (result.errors) {
        showError(result.errors)
      } else {
        showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.update_success'))
        onClose()
      }
    }
  }, [productionAssignedIds, destinationBillId])

  return (
    <FormLayout //
      title={title}
      appearance={{ type: 'DIALOG', open: open, onClose: onClose }}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            clickHandler: () => {
              moveProductionAssigned()
            },
            text: t('commons.confirm'),
          }}
          cancelButton={{ clickHandler: onClose }}
        />
      }
    >
      {loading && <HICLoading />}
      <Stack minWidth={{ xs: '100%', sm: 400 }} minHeight={200}>
        <FormItem
          fieldLabel={t('pms.booking.reservation.productions_assigned.action.destination_bill_label')}
          errorMessage={destinationBillId === null ? t('pms.booking.reservation.productions_assigned.action.destination_bill_error') : undefined}
        >
          <SelectReservationDestinationBill //
            bills={bills}
            setDestinationBillId={setDestinationBillId}
            reservationId={reservationId}
            reservedResourceId={reservedResourceId}
            defaultNewBillCategory={originBillCategory}
            reservation={reservation}
            sourceBillId={sourceBillId}
          />
        </FormItem>
      </Stack>
    </FormLayout>
  )
}
